import React, { useCallback, useMemo } from 'react';
import { Box, Toggle, Flex, TextV2 } from '@withjoy/joykit';
import { Info } from '@withjoy/joykit/icons';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { useTranslation } from '@shared/core';
import type { FormikProps } from 'formik';
import type { AddCashFundDialogFields, CreditCardCurrencyMismatch } from '../../../../types';
import { useHistory } from '@shared/core';
import { useLocation } from '@react-router';
import { useCreditCardSetupTelemetry } from '@apps/registry/admin/components/CreditCardSetup/CreditCardSetUp.telemetry';
import { styles } from '../../CashFundPaymentMethod.style';
import { ExpressPayLogos } from '@shared/components/ExpressPayLogos';

type CreditCardToggleProps = Readonly<{
  formik: FormikProps<AddCashFundDialogFields>;
  canEnableCreditCard: boolean;
  /**
   * Lets a host set up their payout account without leaving the current flow. When omitted the
   * toggle falls back to the `?setupCreditCard=true` URL hop, which opens the standalone dialog.
   */
  onRequestSetup?: () => void;
  /**
   * NREG-3466: set when the registry's currency does not match the payout account's. Cards stay off
   * and payout setup cannot fix it, so the toggle explains why instead of prompting for setup. This
   * is event-level, so when it is set it is true of every fund, not just this one.
   */
  currencyMismatch?: CreditCardCurrencyMismatch | null;
  /**
   * True while the payout account is still being fetched. Eligibility fails closed during that
   * window, so the toggle would otherwise read as "not set up" and send an already-set-up host into
   * the Stripe flow. Hold it inert until we know.
   */
  isEligibilityLoading?: boolean;
}>;

export const CreditCardToggle: React.FC<CreditCardToggleProps> = props => {
  const { canEnableCreditCard, onRequestSetup, currencyMismatch, isEligibilityLoading } = props;
  const location = useLocation();
  const history = useHistory();
  const telemetry = useCreditCardSetupTelemetry();
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { creditCardOption, recommended, fastCheckoutWith } = getCashFundDialogTranslations();
  const { tq } = useTranslation('sharedRegistry');
  const setUpCreditCardLocation = useMemo(
    () => (location.search ? `${location.pathname}${location.search}&setupCreditCard=true` : `${location.pathname}?setupCreditCard=true`),
    [location],
  );

  const onToggleChange = useCallback(
    e => {
      // Checked first: neither an unresolved payout account nor a currency mismatch is something the
      // setup flow can resolve here, so the toggle stays inert in both cases. The notice below
      // carries the explanation for a mismatch.
      if (isEligibilityLoading || currencyMismatch) {
        return;
      }
      if (canEnableCreditCard) {
        return props.formik.handleChange(e);
      }
      telemetry.toggleCreditCardOption();
      if (onRequestSetup) {
        return onRequestSetup();
      }
      return history.push(setUpCreditCardLocation);
    },
    [canEnableCreditCard, currencyMismatch, isEligibilityLoading, history, setUpCreditCardLocation, props.formik, telemetry, onRequestSetup],
  );

  return (
    <Box marginTop={5}>
      <Flex __css={styles.toggleWrapper({ isFeatured: !props.formik.values.enableJoyCredit })}>
        <Flex
          flexDirection="column"
          gap={2}
        >
          <Flex alignItems="center">
            <TextV2
              typographyVariant="hed1"
              marginRight={3}
              whiteSpace={'nowrap'}
            >
              {creditCardOption}
            </TextV2>
            {!props.formik.values.enableJoyCredit && (
              <Box
                border="1px solid"
                borderColor="#4751F8"
                borderRadius="4px"
                paddingX={1}
              >
                <TextV2
                  typographyVariant="label3"
                  color="#4751F8"
                >
                  {recommended}
                </TextV2>
              </Box>
            )}
          </Flex>
          <TextV2
            typographyVariant="label1"
            color="mono8"
          >
            {tq`No pesky redirects. Automatic tracking. Transaction fees apply.`}
          </TextV2>
          <Flex alignItems="center">
            <TextV2
              typographyVariant="label1"
              color="mono14"
              marginRight={2}
            >
              {fastCheckoutWith}
            </TextV2>
            <ExpressPayLogos currencyCode={props.formik.values.currencyCode} />
          </Flex>
          {currencyMismatch && (
            <Flex
              gap={2}
              alignItems="flex-start"
            >
              <Info
                size={16}
                color="mono12"
                css={{ flexShrink: 0, marginTop: '2px' }}
              />
              <TextV2
                typographyVariant="label1"
                color="mono12"
              >
                {tq`Card contributions are off. Your registry collects in ${currencyMismatch.registryCurrencyCode}, but your payout account is in ${currencyMismatch.payoutCurrencyCode}.`}
              </TextV2>
            </Flex>
          )}
        </Flex>
        <Flex justifyContent="center">
          <Toggle
            label=""
            hideLabel={true}
            checked={props.formik.values.enableJoyCredit}
            name="enableJoyCredit"
            onChange={onToggleChange}
            disabled={!!currencyMismatch || !!isEligibilityLoading}
          />
        </Flex>
      </Flex>
    </Box>
  );
};
